/** ADR-0020 — HIS broker admin: choose the backing HIS, STT policy, credentials, sync monitoring. */
import { apiClient } from "./client";

/**
 * One selectable HIS CHANNEL. A vendor's sandbox is its own channel, not a vendor plus an
 * environment flag — see #18890 and the `HisType` enum on the backend.
 */
export type HisType = "MQ" | "LEO" | "MQ_SANDBOX" | "LEO_SANDBOX" | "NATIVE";

/** The company behind a channel: MQ and MQ_SANDBOX share one, NATIVE has none. */
export function hisVendor(t: HisType): "MQ" | "LEO" | "NONE" {
  if (t === "MQ" || t === "MQ_SANDBOX") return "MQ";
  if (t === "LEO" || t === "LEO_SANDBOX") return "LEO";
  return "NONE";
}

export function isSandbox(t: HisType): boolean {
  return t === "MQ_SANDBOX" || t === "LEO_SANDBOX";
}

/**
 * A clinic finishing its trial: same vendor, sandbox -> production. The one external-to-external
 * move allowed in a single step; everything else still goes through "Không có" first.
 */
export function isGoLive(from: HisType, to: HisType): boolean {
  return isSandbox(from) && !isSandbox(to) && hisVendor(from) !== "NONE" && hisVendor(from) === hisVendor(to);
}

export interface CredentialField {
  key: string;
  label: string;
  secret: boolean;
}

export interface HisProvider {
  code: HisType;
  displayName: string;
  external: boolean;
  capabilities: string[];
  credentialFields: CredentialField[];
  /** Non-null only for a sandbox: the simulator address the channel already knows. */
  defaultBaseUrl: string | null;
}

export interface HisBinding {
  hisType: HisType;
  baseUrl: string | null;
  sttSourceSameday: string;
  sttFuture: string;
  sttSyncTime: string;
  sttReset: string;
  writeMode: string;
  // AUTO = scheduler nền tự đẩy (tách biệt khỏi web); MANUAL = nhân viên bấm đẩy từng lịch.
  deliveryMode: "AUTO" | "MANUAL";
  enabled: boolean; // = "đang kết nối" (cho phép đặt lịch / đẩy lịch sang HIS)
  hasCredentials: boolean;
  synced: boolean; // đã đồng bộ metadata ⇒ hisType bị khóa (chỉ kết nối/ngắt + đồng bộ)
}

/** Combined metadata sync result (khoa/phòng/dịch vụ + phường + số ánh xạ Geo). */
/**
 * #18884 — `ran` says a sync was ATTEMPTED, not that it worked. It was always true, which is how a
 * sync that never reached the HIS reported "Khoa 0 · Phòng 0 · Dịch vụ 0" as a result. `status` is
 * the answer to whether it worked; `SUCCESS_EMPTY` means the HIS genuinely publishes none.
 */
export type CatalogSyncStatus = "NOT_RUN" | "SUCCESS" | "SUCCESS_EMPTY" | "FAILED";

export interface MetadataSyncSummary {
  ran: boolean;
  departments: number;
  rooms: number;
  services: number;
  wards: number;
  geoMapped: number;
  status: CatalogSyncStatus;
  /** Which catalogue failed and why, e.g. `department=no_result_payload`. Null unless FAILED. */
  failureReason: string | null;
}

export interface HisBindingUpdate {
  hisType: HisType;
  baseUrl?: string | null;
  sttSourceSameday?: string;
  sttFuture?: string;
  sttSyncTime?: string;
  sttReset?: string;
  writeMode?: string;
  deliveryMode?: "AUTO" | "MANUAL";
  enabled?: boolean;
  credentials?: Record<string, string>;
}

export interface SyncedBooking {
  appointmentId: string;
  patientId: string;
  connector: string;
  bookingDate: string | null;
  hisBookingId: string | null;
  stt: number | null;
  status: string;
  attempts: number;
  lastError: string | null;
  updatedAt: string;
  /** "BOOK" | "CANCEL" (#18676) — which verb `status` is reporting on. */
  action: "BOOK" | "CANCEL";
}

/** One HIS→Addy Geo alias — a HIS location code and the canonical Addy Geo code/name it maps to. */
export interface HisGeoAlias {
  hisCode: string;
  geoCode: number;
  geoName: string;
}

/** Result of a BHYT card check on the external HIS (validity window + registration place). */
export interface HisBhytStatus {
  valid: boolean;
  cardNo: string | null;
  holderName: string | null;
  birthDate: string | null;
  validFrom: string | null;
  validTo: string | null;
  registrationPlaceCode: string | null;
  receptionEligible: number | null;
  note: string | null;
}

/** A patient's record as it exists on the external HIS (found by CCCD / HIS code). */
export interface HisPatientRecord {
  hisPatientCode: string | null;
  fullName: string | null;
  gender: string | null; // M | F
  birthYear: number | null;
  birthDate: string | null;
  mobile: string | null;
  socialId: string | null;
  bhytCode: string | null;
  address: string | null;
  provinceCode: string | null;
  provinceName: string | null;
  wardCode: string | null;
  wardName: string | null;
}

/** One signed HSBA document fetched from the external HIS (kết quả khám / toa thuốc ký số). */
export interface HisExamDocument {
  documentId: number | null;
  category: string | null; // CLS | THUOC | OTHER
  phieuName: string | null;
  departmentName: string | null;
  doctorName: string | null;
  signDate: string | null;
  signed: boolean | null;
  hisPatientCode: string | null;
  encounterCode: string | null;
  pdfFileName: string | null;
  pdfBase64: string | null;
}

/** One clinical encounter from the patient's history on the external HIS (Leo). */
export interface HisEncounter {
  examDate: string | null;
  clinicRoom: string | null;
  doctorName: string | null;
  diagnosis: string | null;
  services: {
    name: string | null;
    unit: string | null;
    quantity: string | null;
    orderDate: string | null;
    resultImage: string | null;
  }[];
  drugs: {
    name: string | null;
    unit: string | null;
    quantity: string | null;
    daysOfUse: number | null;
    instruction: string | null;
    prescribeDate: string | null;
  }[];
}

/** One medication line of a prescription on the external HIS. */
export interface HisPrescriptionDrug {
  name: string | null;
  quantity: number | null;
  unit: string | null;
  dosage: string | null; // morning^noon^afternoon^night, e.g. "1^0^1^0"
  createDate: string | null;
}

export interface SyncedPatient {
  patientId: string;
  fullName: string;
  patientCode: string;
  connector: string;
  hisPatientCode: string | null;
  status: string;
  lastSyncedAt: string | null;
  lastError: string | null;
  updatedAt: string;
}

export interface IntegrationEvent {
  id: string;
  connector: string;
  operation: string;
  correlationId: string | null;
  direction: string;
  httpStatus: number | null;
  ok: boolean;
  latencyMs: number | null;
  errorCode: string | null;
  /** URL that was called (#19086) — null when unknown or nothing was sent. */
  endpoint: string | null;
  createdAt: string;
}

export interface DeliverySummary {
  processed: number;
  confirmed: number;
  rejected: number;
  retriedOrFailed: number;
  pending: number;
}

/** Matches vn.addy.his.integration.his.SyncedEntityType — sent verbatim as the path segment. */
export type CatalogEntityType = "DEPARTMENT" | "EXAM_ROOM" | "SERVICE";

export interface CatalogSyncSummary {
  departments: number;
  rooms: number;
  services: number;
  ran: boolean;
}

export interface LocationSyncSummary {
  provincesSynced: number;
  wardsSynced: number;
  ran: boolean;
}

/** Result of rebuilding the HIS↔Addy Geo location cross-walk (his_location_map). */
export interface LocationMapSummary {
  connector: string;
  provincesMapped: number;
  wardsMapped: number;
  unmatched: number;
  totalHisRows: number;
  unmatchedSample: string[];
}

export interface SyncedCatalogRow {
  id: string;
  name: string;
  code: string;
  sourceSystem: string;
  externalCode: string;
  active: boolean;
  locallyOverridden: boolean;
  syncedAt: string | null;
}

/**
 * A province/ward option from the connector's own address dictionary (`his_location_catalog`).
 * For a tenant bound to an external HIS, this — not Addy Geo — is the address vocabulary: the
 * tenant uses Addy only for booking (product decision, 2026-08-22), so patient province/ward only
 * ever needs to be valid for this one connector. `code` is the connector's own id.
 */
export interface HisLocationOption {
  code: string;
  name: string;
}

/** One bookable time-frame on the external HIS for a room + date (Phase 2/6, #18648). */
export interface HisTimeFrame {
  startTime: string; // "HH:mm"
  endTime: string;
  available: boolean;
  slotNumber: string | null;
}

export interface HisTimeSlots {
  roomId: string;
  date: string;
  slots: HisTimeFrame[];
}

const BASE = "/v1/clinic/settings/his-integration";

export const hisIntegrationApi = {
  providers: () => apiClient.get<HisProvider[]>(`${BASE}/providers`).then((r) => r.data),
  get: () => apiClient.get<HisBinding>(BASE).then((r) => r.data),
  update: (body: HisBindingUpdate) => apiClient.put<HisBinding>(BASE, body).then((r) => r.data),
  connect: () => apiClient.post<HisBinding>(`${BASE}/connect`).then((r) => r.data),
  disconnect: () => apiClient.post<HisBinding>(`${BASE}/disconnect`).then((r) => r.data),
  runMetadataSync: () =>
    apiClient.post<MetadataSyncSummary>(`${BASE}/metadata-sync/run`).then((r) => r.data),
  syncedBookings: (status?: string, appointmentId?: string) =>
    apiClient
      .get<SyncedBooking[]>(`${BASE}/synced-bookings`, {
        params: { status, appointmentId, limit: 200 },
      })
      .then((r) => r.data),
  syncedPatients: (status?: string) =>
    apiClient
      .get<SyncedPatient[]>(`${BASE}/synced-patients`, { params: { status, limit: 200 } })
      .then((r) => r.data),
  logs: (failuresOnly?: boolean) =>
    apiClient
      .get<IntegrationEvent[]>(`${BASE}/logs`, { params: { failuresOnly, limit: 100 } })
      .then((r) => r.data),
  runSync: () => apiClient.post<DeliverySummary>(`${BASE}/sync/run`).then((r) => r.data),
  patientLookup: (params: { socialId?: string | null; patientCode?: string | null }) =>
    apiClient
      .get<HisPatientRecord | "">(`${BASE}/patient-lookup`, { params })
      .then((r) => (r.data ? (r.data as HisPatientRecord) : null)),
  examResults: (params: {
    patientCode?: string | null;
    socialId?: string | null;
    fromDate?: string | null;
    toDate?: string | null;
    documentType?: string;
  }) =>
    apiClient
      .get<HisExamDocument[]>(`${BASE}/exam-results`, { params })
      .then((r) => r.data),
  prescription: (patientCode: string, encounterCode: string) =>
    apiClient
      .get<HisPrescriptionDrug[]>(`${BASE}/prescription`, { params: { patientCode, encounterCode } })
      .then((r) => r.data),
  bhytCheck: (patientCode: string) =>
    apiClient
      .get<HisBhytStatus | "">(`${BASE}/bhyt-check`, { params: { patientCode } })
      .then((r) => (r.data ? (r.data as HisBhytStatus) : null)),
  locationMap: () =>
    apiClient
      .get<HisGeoAlias[]>(`${BASE}/location-map`)
      .then((r) => r.data),
  medicalHistory: (params: { socialId?: string | null; patientCode?: string | null }) =>
    apiClient
      .get<HisEncounter[]>(`${BASE}/medical-history`, { params })
      .then((r) => r.data),
  runCatalogSync: () =>
    apiClient.post<CatalogSyncSummary>(`${BASE}/catalog-sync/run`).then((r) => r.data),
  runLocationSync: () =>
    apiClient.post<LocationSyncSummary>(`${BASE}/location-sync/run`).then((r) => r.data),
  runLocationMap: () =>
    apiClient.post<LocationMapSummary>(`${BASE}/location-map/run`).then((r) => r.data),
  catalogSynced: (entityType: CatalogEntityType) =>
    apiClient
      .get<SyncedCatalogRow[]>(`${BASE}/catalog-sync/${entityType}`)
      .then((r) => r.data),
  setCatalogOverride: (entityType: CatalogEntityType, id: string, overridden: boolean) =>
    apiClient
      .put<void>(`${BASE}/catalog-sync/${entityType}/${id}/override`, { overridden })
      .then((r) => r.data),
  locationProvinces: () =>
    apiClient.get<HisLocationOption[]>(`${BASE}/locations/provinces`).then((r) => r.data),
  locationWards: (provinceCode: string) =>
    apiClient
      .get<HisLocationOption[]>(`${BASE}/locations/provinces/${provinceCode}/wards`)
      .then((r) => r.data),
  /**
   * Real time-frame availability for one room + date on the connected external HIS (LEO today —
   * Phase 6, extends #18648's staff endpoint into the booking form). 204 (returned as "" by axios)
   * when the tenant isn't LEO-bound, disabled, or the room was never catalog-synced — callers fall
   * back to the existing native time picker in that case, never treat this as an error.
   */
  timeSlots: (roomId: string, date: string) =>
    apiClient
      .get<HisTimeSlots | "">(`${BASE}/time-slots`, { params: { roomId, date } })
      .then((r) => (r.data ? (r.data as HisTimeSlots) : null)),
};

export const hisIntegrationQueryKeys = {
  binding: () => ["his-integration", "binding"] as const,
  providers: () => ["his-integration", "providers"] as const,
  bookings: (status?: string) => ["his-integration", "bookings", status ?? "all"] as const,
  bookingFor: (appointmentId: string) =>
    ["his-integration", "bookings", "appointment", appointmentId] as const,
  examResults: (key: string) => ["his-integration", "exam-results", key] as const,
  patientLookup: (key: string) => ["his-integration", "patient-lookup", key] as const,
  prescription: (encounterCode: string) =>
    ["his-integration", "prescription", encounterCode] as const,
  bhytCheck: (patientCode: string) => ["his-integration", "bhyt-check", patientCode] as const,
  locationMap: () => ["his-integration", "location-map"] as const,
  medicalHistory: (key: string) => ["his-integration", "medical-history", key] as const,
  patients: (status?: string) => ["his-integration", "patients", status ?? "all"] as const,
  logs: (failuresOnly?: boolean) => ["his-integration", "logs", !!failuresOnly] as const,
  catalogSynced: (entityType: CatalogEntityType) =>
    ["his-integration", "catalog-synced", entityType] as const,
  locationProvinces: () => ["his-integration", "locations", "provinces"] as const,
  locationWards: (provinceCode: string | null | undefined) =>
    ["his-integration", "locations", "wards", provinceCode ?? null] as const,
  timeSlots: (roomId: string | null | undefined, date: string | null | undefined) =>
    ["his-integration", "time-slots", roomId ?? null, date ?? null] as const,
};
